import React, { useState, useEffect } from 'react';
import { X, Globe2, Save, Lock, Globe } from 'lucide-react';
import { cn } from '../utils/cn';

type TemplateVisibility = 'private' | 'public';

interface SaveSiteAsTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    name: string;
    description?: string;
    tags?: string;
    isPublic: boolean;
    isSystem: boolean;
  }) => void;
  isLoading?: boolean;
  siteName: string;
  isAdmin?: boolean;
}

export const SaveSiteAsTemplateModal: React.FC<SaveSiteAsTemplateModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  siteName,
  isAdmin = false,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tags, setTags] = useState('');
  const [visibility, setVisibility] = useState<TemplateVisibility>('private');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setName(`${siteName} Template`);
      setDescription(`Website template based on "${siteName}"`);
      setTags('');
      setVisibility('private');
      setErrors({});
    }
  }, [isOpen, siteName]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});

    // Validation
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = 'Template name is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    onSubmit({
      name: name.trim(),
      description: description.trim() || undefined,
      tags: tags.trim() || undefined,
      isPublic: visibility === 'public',
      isSystem: false,
    });
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className={cn(
        'modal-backdrop',
        'flex items-center justify-center z-50 p-4',
        isOpen ? 'animate-fade-in' : ''
      )}
      onClick={handleBackdropClick}
    >
      <div
        className={cn(
          'modal-content',
          'max-w-lg w-full max-h-screen overflow-y-auto',
          isOpen ? 'animate-slide-up' : ''
        )}
      >
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary-100 rounded-lg">
              <Globe2 className="h-6 w-6 text-primary-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Save as Site Template</h2>
              <p className="text-sm text-gray-500 mt-1">
                Create a reusable template from this website
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className={cn(
              'p-2 text-gray-400 hover:text-gray-600 rounded-lg',
              'hover:bg-gray-100 transition-all duration-200',
              'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2'
            )}
            disabled={isLoading}
          >
            <X size={20} />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Template Name */}
          <div>
            <label
              htmlFor="templateName"
              className="block text-sm font-semibold text-gray-700 mb-2"
            >
              Template Name <span className="text-red-500">*</span>
            </label>
            <input
              id="templateName"
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              className={cn(
                'input-field',
                errors.name && 'border-red-300 focus:border-red-500 focus:ring-red-500'
              )}
              placeholder="Enter template name"
              disabled={isLoading}
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
          </div>

          {/* Description */}
          <div>
            <label
              htmlFor="templateDescription"
              className="block text-sm font-semibold text-gray-700 mb-2"
            >
              Description
            </label>
            <textarea
              id="templateDescription"
              value={description}
              onChange={e => setDescription(e.target.value)}
              className="input-field resize-none"
              rows={3}
              placeholder="Describe what this template is for..."
              disabled={isLoading}
            />
            <p className="mt-1 text-xs text-gray-500">
              Help others understand when to use this template
            </p>
          </div>

          {/* Tags */}
          <div>
            <label
              htmlFor="templateTags"
              className="block text-sm font-semibold text-gray-700 mb-2"
            >
              Tags
            </label>
            <input
              id="templateTags"
              type="text"
              value={tags}
              onChange={e => setTags(e.target.value)}
              className="input-field"
              placeholder="e.g., business, portfolio, e-commerce"
              disabled={isLoading}
            />
            <p className="mt-1 text-xs text-gray-500">
              Comma-separated tags to help categorize and find this template
            </p>
          </div>

          {/* Visibility - Radio Buttons (only show for admins) */}
          {isAdmin && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-3">Visibility</label>
              <div className="space-y-3">
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="visibility"
                    value="private"
                    checked={visibility === 'private'}
                    onChange={() => setVisibility('private')}
                    className="mt-0.5 h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                    disabled={isLoading}
                  />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <Lock size={16} className="text-gray-400" />
                      <span className="text-sm font-medium text-gray-900">Private</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Only you can see and use this template
                    </p>
                  </div>
                </label>

                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="visibility"
                    value="public"
                    checked={visibility === 'public'}
                    onChange={() => setVisibility('public')}
                    className="mt-0.5 h-4 w-4 text-primary-600 border-gray-300 focus:ring-primary-500"
                    disabled={isLoading}
                  />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <Globe size={16} className="text-gray-400" />
                      <span className="text-sm font-medium text-gray-900">Public</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Anyone can see and use this template
                    </p>
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 mt-6 border-t border-gray-100">
            <button type="button" onClick={onClose} className="btn-secondary" disabled={isLoading}>
              Cancel
            </button>
            <button
              type="submit"
              className={cn(
                'btn-primary flex items-center space-x-2',
                isLoading && 'opacity-50 cursor-not-allowed'
              )}
              disabled={isLoading || !name.trim()}
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 spinner"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save size={16} />
                  <span>Save Template</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
